import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Calendar,
  Search,
  Filter,
  ChevronLeft,
  ChevronRight,
  Eye,
  X,
  CheckCircle,
  AlertCircle,
  Clock,
  User,
  Phone,
  Mail,
  Trash2,
  Edit,
  Save,
  Package,
  DollarSign,
  Building
} from 'lucide-react';
import axios from 'axios';

const ManageAppointments = () => {
  const { user, token } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [filters, setFilters] = useState({
    service_provider_id: '',
    date_filter: 'all',
    status: '',
    appointment_type: 'all'
  });
  const [serviceProviders, setServiceProviders] = useState([]);
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [customFields, setCustomFields] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [appointmentToDelete, setAppointmentToDelete] = useState(null);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);
  
  // Boarding-specific states
  const [spaceCategories, setSpaceCategories] = useState([]);
  const [availableSpaces, setAvailableSpaces] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [serviceCategoryMappings, setServiceCategoryMappings] = useState({});
  const [filteredSpaces, setFilteredSpaces] = useState([]);
  const [formFields, setFormFields] = useState([]);
  const [tourFields, setTourFields] = useState([]);
  const [roomFields, setRoomFields] = useState([]);
  const [specialInstructionFields, setSpecialInstructionFields] = useState([]);
  const [boardingDataLoading, setBoardingDataLoading] = useState(false);
  const [modalDataReady, setModalDataReady] = useState(false); // Track if modal data is loaded
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [selectedServiceOptions, setSelectedServiceOptions] = useState({});
  const [taxSettings, setTaxSettings] = useState({ tax_enabled: false, tax_rate: 0 });

  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
  const primaryColor = 'var(--theme-primary)';

  const statusOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'confirmed', label: 'Confirmed' },
    { value: 'completed', label: 'Completed' },
    { value: 'cancelled', label: 'Cancelled' },
    { value: 'no_show', label: 'No Show' }
  ];

  const dateFilterOptions = [
    { value: 'all', label: 'All Time' },
    { value: 'today', label: 'Today' },
    { value: 'tomorrow', label: 'Tomorrow' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'this_week', label: 'This Week' },
    { value: 'last_week', label: 'Last Week' },
    { value: 'this_month', label: 'This Month' }
  ];

  const appointmentTypeOptions = [
    { value: 'all', label: 'All Types' },
    { value: 'appointment', label: 'Book Appointment' },
    { value: 'boarding', label: 'Book Boarding' },
    { value: 'tour', label: 'Schedule Tour' }
  ];

  const statusColors = {
    scheduled: 'bg-blue-100 text-blue-800',
    confirmed: 'bg-green-100 text-green-800',
    completed: 'bg-gray-100 text-gray-800',
    cancelled: 'bg-red-100 text-red-800',
    no_show: 'bg-yellow-100 text-yellow-800'
  };

  useEffect(() => {
    if (token) {
      fetchServiceProviders();
      fetchCustomFields();
      fetchAppointments();
      fetchBoardingData();
    }
  }, [token, filters]); // Removed currentPage and pageSize from dependencies

  // Store all appointments for pagination
  const [allAppointments, setAllAppointments] = useState([]);

  // Apply pagination when page or pageSize changes
  useEffect(() => {
    if (allAppointments.length > 0) {
      const totalItems = allAppointments.length;
      const calculatedTotalPages = Math.ceil(totalItems / pageSize);
      const startIndex = (currentPage - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      const paginatedAppointments = allAppointments.slice(startIndex, endIndex);
      
      setAppointments(paginatedAppointments);
      setTotalPages(calculatedTotalPages || 1);
      
      // Scroll to top when page changes
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentPage, pageSize, allAppointments]);

  // Helper function to calculate stay duration in hours
  const calculateStayDuration = (dropoffDate, dropoffTime, pickupDate, pickupTime) => {
    if (!dropoffDate || !dropoffTime || !pickupDate || !pickupTime) {
      return 0;
    }
    
    const checkIn = new Date(`${dropoffDate}T${dropoffTime}`);
    const checkOut = new Date(`${pickupDate}T${pickupTime}`);
    
    const durationMs = checkOut - checkIn;
    const durationHours = durationMs / (1000 * 60 * 60);
    
    return Math.max(0, durationHours);
  };

  // Helper function to calculate variant price based on duration
  const calculateVariantPrice = (service, durationHours) => {
    if (!service?.is_variable || !service?.variants || service.variants.length === 0) {
      return service?.price || 0;
    }

    if (durationHours <= 0) {
      return 0;
    }

    const variants = service.variants
      .filter(v => v.is_enabled)
      .map(v => {
        const variantName = v.variant_name.toLowerCase().replace(/\s+/g, ''); // Remove spaces
        let hours = 0;
        
        // More flexible parsing to handle different formats: "1hr", "1 hr", "1Hr", "24hr", etc.
        if (variantName.includes('hr')) {
          const match = variantName.match(/(\d+\.?\d*)hr/);
          if (match) {
            hours = parseFloat(match[1]);
          }
        } else if (variantName.includes('min')) {
          const match = variantName.match(/(\d+\.?\d*)min/);
          if (match) {
            hours = parseFloat(match[1]) / 60;
          }
        }
        
        return { hours, price: v.price, name: v.variant_name };
      })
      .sort((a, b) => b.hours - a.hours); // Sort by hours descending (largest first)

    let remainingHours = durationHours;
    let totalPrice = 0;

    // Greedy algorithm: use largest variants first
    for (const variant of variants) {
      if (variant.hours > 0) {
        const count = Math.floor(remainingHours / variant.hours);
        if (count > 0) {
          totalPrice += count * variant.price;
          remainingHours -= count * variant.hours;
        }
      }
    }

    // Handle remaining hours with smallest variant
    if (remainingHours > 0 && variants.length > 0) {
      const smallestVariant = variants[variants.length - 1];
      const additionalCount = Math.ceil(remainingHours / smallestVariant.hours);
      totalPrice += additionalCount * smallestVariant.price;
    }

    return totalPrice;
  };

  // Helper function to get variant breakdown
  const getVariantBreakdown = (service, durationHours) => {
    if (!service?.is_variable || !service?.variants || service.variants.length === 0) {
      return [];
    }

    if (durationHours <= 0) {
      return [];
    }

    const variants = service.variants
      .filter(v => v.is_enabled)
      .map(v => {
        const variantName = v.variant_name.toLowerCase().replace(/\s+/g, ''); // Remove spaces
        let hours = 0;
        
        // More flexible parsing to handle different formats: "1hr", "1 hr", "1Hr", "24hr", etc.
        if (variantName.includes('hr')) {
          const match = variantName.match(/(\d+\.?\d*)hr/);
          if (match) {
            hours = parseFloat(match[1]);
          }
        } else if (variantName.includes('min')) {
          const match = variantName.match(/(\d+\.?\d*)min/);
          if (match) {
            hours = parseFloat(match[1]) / 60;
          }
        }
        
        return { hours, price: v.price, name: v.variant_name };
      })
      .sort((a, b) => b.hours - a.hours); // Sort by hours descending (largest first)

    let remainingHours = durationHours;
    const breakdown = [];

    // Greedy algorithm: use largest variants first
    for (const variant of variants) {
      if (variant.hours > 0) {
        const count = Math.floor(remainingHours / variant.hours);
        if (count > 0) {
          remainingHours -= count * variant.hours;
          breakdown.push({ ...variant, count });
        }
      }
    }

    // Handle remaining hours with smallest variant
    if (remainingHours > 0 && variants.length > 0) {
      const smallestVariant = variants[variants.length - 1];
      const additionalCount = Math.ceil(remainingHours / smallestVariant.hours);
      const existingEntry = breakdown.find(b => b.name === smallestVariant.name);
      if (existingEntry) {
        existingEntry.count += additionalCount;
      } else {
        breakdown.push({ ...smallestVariant, count: additionalCount });
      }
    }

    return breakdown;
  };

  const fetchBoardingData = async () => {
    setBoardingDataLoading(true);
    try {
      // Fetch space categories
      const categoriesResponse = await axios.get(`${API_BASE_URL}/api/boarding-space-categories`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const categories = categoriesResponse.data.success ? categoriesResponse.data.categories : [];
      setSpaceCategories(categories);

      // Fetch spaces
      const spacesResponse = await axios.get(`${API_BASE_URL}/api/boarding-spaces`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const spaces = spacesResponse.data.success ? spacesResponse.data.spaces : [];
      setAvailableSpaces(spaces);

      // Fetch form fields
      const formResponse = await axios.get(`${API_BASE_URL}/api/boarding-form-fields?section=form`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (formResponse.data.success) {
        setFormFields(formResponse.data.fields);
      }

      const tourResponse = await axios.get(`${API_BASE_URL}/api/boarding-form-fields?section=tour`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (tourResponse.data.success) {
        setTourFields(tourResponse.data.fields);
      }

      const roomResponse = await axios.get(`${API_BASE_URL}/api/boarding-form-fields?section=rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (roomResponse.data.success) {
        setRoomFields(roomResponse.data.fields);
      }

      const specialResponse = await axios.get(`${API_BASE_URL}/api/boarding-form-fields?section=special_instructions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (specialResponse.data.success) {
        setSpecialInstructionFields(specialResponse.data.fields);
      }

      // Fetch boarding settings to get service-category mappings
      const settingsResponse = await axios.get(`${API_BASE_URL}/api/boarding-settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('=== BOARDING SETTINGS API RESPONSE ===');
      console.log('Full response:', settingsResponse.data);
      
      let mappings = {};
      let servicesWithMappings = [];
      
      if (settingsResponse.data.success) {
        const settings = settingsResponse.data.settings || {};
        mappings = settings.service_category_mappings || {};
        
        // Extract tax settings from boarding settings
        setTaxSettings({
          tax_enabled: settings.tax_enabled || false,
          tax_rate: settings.tax_rate || 0
        });
        
        console.log('=== SERVICE-CATEGORY MAPPINGS ===');
        console.log('Mappings:', mappings);
        console.log('Number of mappings:', Object.keys(mappings).length);
        
        setServiceCategoryMappings(mappings);

        // Get service IDs that have NON-EMPTY space category mappings
        const serviceIdsWithMappings = Object.keys(mappings).filter(serviceId => {
          const categories = mappings[serviceId];
          const hasCategories = Array.isArray(categories) && categories.length > 0;
          console.log(`Service ${serviceId}: ${hasCategories ? 'HAS' : 'NO'} space categories`);
          return hasCategories;
        });
        console.log('Service IDs with valid mappings:', serviceIdsWithMappings);

        if (serviceIdsWithMappings.length > 0) {
          const allServiceCategoriesResponse = await axios.get(`${API_BASE_URL}/api/appointment-service-menu-categories`, {
            headers: { Authorization: `Bearer ${token}` }
          });

          if (allServiceCategoriesResponse.data.success) {
            const serviceCategories = allServiceCategoriesResponse.data.menu_categories || [];
            console.log('Service menu categories found:', serviceCategories.length);

            // Fetch services from all categories
            const servicePromises = serviceCategories.map(category =>
              axios.get(`${API_BASE_URL}/api/appointment-service-categories/${category.id}/services`, {
                headers: { Authorization: `Bearer ${token}` }
              }).catch(err => {
                console.error(`Error fetching services for category ${category.id}:`, err);
                return { data: { services: [] } };
              })
            );

            const servicesResponses = await Promise.all(servicePromises);
            const allServices = servicesResponses.flatMap(res => res.data.services || []);
            
            console.log('=== ALL SERVICES FETCHED ===');
            console.log('Total services:', allServices.length);
            
            // Filter to only services that have NON-EMPTY space category mappings
            servicesWithMappings = allServices.filter(service => 
              serviceIdsWithMappings.includes(service.id)
            );
            
            console.log('=== SERVICES WITH MAPPINGS ===');
            console.log('Count:', servicesWithMappings.length);
            console.log('Services:', servicesWithMappings.map(s => ({
              id: s.id,
              name: s.service_name || s.name,
              price: s.price
            })));
            
            setAvailableServices(servicesWithMappings);
          }
        } else {
          console.warn('No services linked to space categories');
          setAvailableServices([]);
        }
      }

      // Return the fresh data so it can be used immediately
      return {
        categories,
        spaces,
        mappings,
        services: servicesWithMappings
      };
    } catch (error) {
      console.error('Error fetching boarding data:', error);
      return {
        categories: [],
        spaces: [],
        mappings: {},
        services: []
      };
    } finally {
      setBoardingDataLoading(false);
    }
  };

  // Helper function to extract customer name from boarding appointment
  const extractCustomerName = (appointment, fields = formFields) => {
    if (!appointment.form_details || Object.keys(appointment.form_details).length === 0) {
      return '-';
    }
    
    // Check if it's a tour appointment
    const isTour = appointment.appointment_type === 'tour' || appointment.reservation_type === 'tour';
    
    // First, try to get values using system variable IDs
    // Use tour-specific IDs for tour appointments, regular IDs for boarding
    const firstName = isTour 
      ? (appointment.form_details['default_tour_first_name'] || '')
      : (appointment.form_details['default_first_name'] || '');
    const lastName = isTour
      ? (appointment.form_details['default_tour_last_name'] || '')
      : (appointment.form_details['default_last_name'] || '');
    
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    
    // Fallback: search through all fields by field name
    let fallbackFirstName = '';
    let fallbackLastName = '';
    let fullName = '';
    
    Object.entries(appointment.form_details).forEach(([fieldId, value]) => {
      const field = fields.find(f => f.id === fieldId);
      const fieldName = field?.field_name?.toLowerCase() || fieldId.toLowerCase();
      const fieldValue = String(value || '').trim();
      
      if (!fieldValue) return;
      
      if (fieldName.includes('first') && fieldName.includes('name')) {
        fallbackFirstName = fieldValue;
      } else if (fieldName.includes('last') && fieldName.includes('name')) {
        fallbackLastName = fieldValue;
      } else if ((fieldName === 'name' || (fieldName.includes('full') && fieldName.includes('name'))) && !fallbackFirstName && !fallbackLastName) {
        fullName = fieldValue;
      }
    });
    
    const combinedName = `${fallbackFirstName} ${fallbackLastName}`.trim();
    return combinedName || fullName || '-';
  };

  const fetchCustomFields = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/appointment-custom-fields`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setCustomFields(response.data.fields);
      }
    } catch (error) {
      console.error('Error fetching custom fields:', error);
    }
  };

  const fetchServiceProviders = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/service-providers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setServiceProviders(response.data.service_providers);
      }
    } catch (error) {
      console.error('Error fetching service providers:', error);
    }
  };

  const fetchAppointments = async () => {
    setLoading(true);
    try {
      // Calculate date based on filter
      let bookingDate = '';
      const today = new Date();
      const formatDate = (date) => date.toISOString().split('T')[0];

      switch (filters.date_filter) {
        case 'today':
          bookingDate = formatDate(today);
          break;
        case 'tomorrow':
          const tomorrow = new Date(today);
          tomorrow.setDate(today.getDate() + 1);
          bookingDate = formatDate(tomorrow);
          break;
        case 'yesterday':
          const yesterday = new Date(today);
          yesterday.setDate(today.getDate() - 1);
          bookingDate = formatDate(yesterday);
          break;
        case 'this_week':
        case 'last_week':
        case 'this_month':
          // For these, we'll need to filter on the frontend after fetching
          bookingDate = '';
          break;
        default:
          bookingDate = '';
      }

      const params = {
        service_provider_id: filters.service_provider_id,
        booking_date: bookingDate,
        status: filters.status
      };

      // Fetch both regular appointments and boarding reservations
      let appointmentResponse;
      let boardingResponse = { data: { success: true, reservations: [] } };
      
      // Fetch ALL appointments (no pagination params - we'll paginate on frontend)
      try {
        appointmentResponse = await axios.get(`${API_BASE_URL}/api/appointment-bookings`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
      } catch (error) {
        console.error('Error fetching appointments:', error);
        // Set empty response on failure
        appointmentResponse = { data: { success: true, bookings: [], total_pages: 1 } };
      }

      // Fetch boarding reservations (optional, may not exist)
      try {
        boardingResponse = await axios.get(`${API_BASE_URL}/api/boarding-reservations`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { status: filters.status } // Only pass status, not pagination params
        });
      } catch (error) {
        // Silently fail - boarding endpoint may not exist or may be empty
        console.log('Boarding reservations endpoint not available or empty');
      }

      if (appointmentResponse.data.success) {
        let filteredAppointments = [...(appointmentResponse.data.bookings || [])];
        
        // Add boarding reservations with appointment_type field
        if (boardingResponse.data.success && boardingResponse.data.reservations) {
          const boardingAppointments = boardingResponse.data.reservations.map(reservation => ({
            ...reservation,
            appointment_type: reservation.reservation_type || 'boarding',
            booking_date: reservation.check_in_date || reservation.dropoff_date,
            booking_time: reservation.check_in_time || reservation.dropoff_time,
            appointment_number: reservation.reservation_number,
            // Map fields for consistency
            customer_name: reservation.customer_name || reservation.form_data?.name,
            customer_email: reservation.customer_email || reservation.form_data?.email,
            customer_phone: reservation.customer_phone || reservation.form_data?.phone
          }));
          filteredAppointments = [...filteredAppointments, ...boardingAppointments];
        }

        // Frontend filtering for week/month ranges
        if (filters.date_filter === 'this_week') {
          const startOfWeek = new Date(today);
          startOfWeek.setDate(today.getDate() - today.getDay());
          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          
          filteredAppointments = filteredAppointments.filter(apt => {
            const aptDate = new Date(apt.booking_date);
            return aptDate >= startOfWeek && aptDate <= endOfWeek;
          });
        } else if (filters.date_filter === 'last_week') {
          const startOfLastWeek = new Date(today);
          startOfLastWeek.setDate(today.getDate() - today.getDay() - 7);
          const endOfLastWeek = new Date(startOfLastWeek);
          endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
          
          filteredAppointments = filteredAppointments.filter(apt => {
            const aptDate = new Date(apt.booking_date);
            return aptDate >= startOfLastWeek && aptDate <= endOfLastWeek;
          });
        } else if (filters.date_filter === 'this_month') {
          const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
          const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          
          filteredAppointments = filteredAppointments.filter(apt => {
            const aptDate = new Date(apt.booking_date);
            return aptDate >= startOfMonth && aptDate <= endOfMonth;
          });
        }

        // Filter by appointment type
        if (filters.appointment_type && filters.appointment_type !== 'all') {
          filteredAppointments = filteredAppointments.filter(apt => {
            const type = apt.appointment_type || apt.reservation_type || 'appointment';
            return type === filters.appointment_type;
          });
        }

        // Store all filtered appointments for pagination
        setAllAppointments(filteredAppointments);
      }
    } catch (error) {
      console.error('Error fetching appointments:', error);
      // Only show error if it's a critical failure, not transient
      if (error.response && error.response.status >= 500) {
        showMessage('error', 'Failed to load appointments');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (appointmentId, newStatus) => {
    try {
      const response = await axios.patch(
        `${API_BASE_URL}/api/appointment-bookings/${appointmentId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', 'Status updated successfully');
        fetchAppointments();
      }
    } catch (error) {
      showMessage('error', 'Failed to update status');
    }
  };

  const showMessage = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage({ type: '', text: '' }), 4000);
  };

  const handleViewDetails = async (appointment) => {
    console.log('=== OPENING MODAL ===');
    
    setSelectedAppointment(appointment);
    setIsEditMode(false);
    setShowDetailModal(true);
    setModalDataReady(false); // Show loading state
    
    // Check if it's a boarding appointment
    const isBoardingAppointment = appointment.appointment_type === 'boarding' || appointment.reservation_type === 'boarding';
    
    if (isBoardingAppointment) {
      console.log('🔄 Fetching boarding data...');
      
      // Fetch all boarding data fresh
      const freshData = await fetchBoardingData();
      
      console.log('✅ Data loaded:', {
        services: freshData.services.length,
        spaces: freshData.spaces.length,
        mappings: Object.keys(freshData.mappings).length
      });
      
      const serviceId = appointment.selected_service_id || appointment.selected_service?.id;
      
      // Use the fresh spaces data to filter based on service
      const linkedCategories = freshData.mappings[serviceId] || [];
      const categoryArray = Array.isArray(linkedCategories) ? linkedCategories : (linkedCategories ? [linkedCategories] : []);
      
      let relevantSpaces = freshData.spaces;
      if (categoryArray.length > 0) {
        relevantSpaces = freshData.spaces.filter(space => 
          categoryArray.includes(space.category_id)
        );
      }
      
      // Always include the currently assigned space
      if (appointment.assigned_space_id) {
        const currentSpace = freshData.spaces.find(s => s.id === appointment.assigned_space_id);
        if (currentSpace && !relevantSpaces.find(s => s.id === appointment.assigned_space_id)) {
          relevantSpaces = [currentSpace, ...relevantSpaces];
        }
      }
      
      setFilteredSpaces(relevantSpaces);
      
      setEditForm({
        dropoff_date: appointment.dropoff_date,
        dropoff_time: appointment.dropoff_time,
        pickup_date: appointment.pickup_date,
        pickup_time: appointment.pickup_time,
        assigned_space_id: appointment.assigned_space_id,
        selected_service_id: serviceId,
        selected_service: appointment.selected_service,
        status: appointment.status,
        form_details: appointment.form_details || {},
        room_details: appointment.room_details || {},
        special_instructions: appointment.special_instructions || {}
      });
      
      // Data is ready, show the form
      setModalDataReady(true);
      console.log('✅ Modal data ready');
    } else {
      setEditForm({
        booking_date: appointment.booking_date,
        booking_time: appointment.booking_time,
        service_provider_id: appointment.service_provider_id,
        status: appointment.status,
        customer_details: appointment.customer_details || {},
        booking_notes: appointment.booking_notes || ''
      });
      setModalDataReady(true);
    }
  };

  const updateFilteredSpaces = (serviceId, currentSpaceId = null) => {
    if (!serviceId) {
      // If no service selected, show all spaces
      setFilteredSpaces(availableSpaces);
      return;
    }

    // Get categories linked to this service
    const linkedCategories = serviceCategoryMappings[serviceId] || [];
    
    // Handle both array and single value formats
    const categoryArray = Array.isArray(linkedCategories) ? linkedCategories : (linkedCategories ? [linkedCategories] : []);
    
    if (categoryArray.length === 0) {
      // If no categories mapped, show all spaces
      setFilteredSpaces(availableSpaces);
      return;
    }
    
    // Filter spaces that belong to these categories
    let relevantSpaces = availableSpaces.filter(space => 
      categoryArray.includes(space.category_id)
    );
    
    // Always include the currently assigned space even if not in filtered list
    if (currentSpaceId) {
      const currentSpace = availableSpaces.find(s => s.id === currentSpaceId);
      if (currentSpace && !relevantSpaces.find(s => s.id === currentSpaceId)) {
        relevantSpaces = [currentSpace, ...relevantSpaces];
      }
    }
    
    setFilteredSpaces(relevantSpaces);
  };

  const handleServiceChange = (serviceId) => {
    const selectedServiceObj = availableServices.find(s => s.id === serviceId);
    
    // Update filtered spaces based on new service (don't pass current space since we're changing service)
    updateFilteredSpaces(serviceId, null);
    
    // Update form with new service and reset space selection
    setEditForm({
      ...editForm, 
      selected_service_id: serviceId,
      selected_service: selectedServiceObj,
      assigned_space_id: '', // Reset space when service changes
    });
    
    // If service has options, show options modal
    if (selectedServiceObj?.has_options && selectedServiceObj?.options?.length > 0) {
      // Try to carry over previous selections
      const previousOptions = selectedAppointment.selected_service?.selectedOptions || {};
      const newOptions = {};
      
      // Map previous selections to new service options where option names match
      selectedServiceObj.options.forEach(option => {
        const previousValue = previousOptions[option.option_name];
        if (previousValue !== undefined) {
          newOptions[option.option_name] = previousValue;
        }
      });
      
      setSelectedServiceOptions(newOptions);
      setShowOptionsModal(true);
    }
  };
  
  const handleOptionChange = (optionName, value) => {
    setSelectedServiceOptions({
      ...selectedServiceOptions,
      [optionName]: value
    });
  };
  
  const handleOptionsConfirm = () => {
    // Save options to edit form
    setEditForm({
      ...editForm,
      selected_service: {
        ...editForm.selected_service,
        selectedOptions: selectedServiceOptions
      }
    });
    setShowOptionsModal(false);
  };

  const openDeleteModal = (appointment) => {
    setAppointmentToDelete(appointment);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setAppointmentToDelete(null);
  };

  const handleDeleteAppointment = async () => {
    if (!appointmentToDelete) return;

    try {
      // Check if this is a boarding reservation
      const isBoardingAppointment = appointmentToDelete.appointment_type === 'boarding' || 
                                     appointmentToDelete.reservation_type === 'boarding';
      
      const endpoint = isBoardingAppointment 
        ? `${API_BASE_URL}/api/boarding-reservations/${appointmentToDelete.id}`
        : `${API_BASE_URL}/api/appointment-bookings/${appointmentToDelete.id}`;

      await axios.delete(endpoint, { headers: { Authorization: `Bearer ${token}` } });
      
      showMessage('success', isBoardingAppointment ? 'Boarding reservation deleted successfully' : 'Appointment deleted successfully');
      fetchAppointments(); // This will refresh the list and calendar
      setShowDetailModal(false);
      closeDeleteModal();
    } catch (error) {
      console.error('Delete error:', error);
      showMessage('error', 'Failed to delete appointment');
      closeDeleteModal();
    }
  };

  const handleDeleteAllAppointments = async () => {
    try {
      const response = await axios.delete(
        `${API_BASE_URL}/api/appointment-bookings`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showMessage('success', `All appointments deleted successfully (${response.data.deleted_count} appointments)`);
      fetchAppointments();
      setShowDeleteAllModal(false);
    } catch (error) {
      showMessage('error', 'Failed to delete all appointments');
      setShowDeleteAllModal(false);
    }
  };

  const handleEditAppointment = () => {
    setIsEditMode(true);
  };

  const handleSaveEdit = async () => {
    try {
      const isBoardingAppointment = selectedAppointment.appointment_type === 'boarding' || selectedAppointment.reservation_type === 'boarding';
      
      const endpoint = isBoardingAppointment 
        ? `${API_BASE_URL}/api/boarding-reservations/${selectedAppointment.id}`
        : `${API_BASE_URL}/api/appointment-bookings/${selectedAppointment.id}`;

      const response = await axios.patch(
        endpoint,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showMessage('success', isBoardingAppointment ? 'Reservation updated successfully' : 'Appointment updated successfully');
        setIsEditMode(false);
        fetchAppointments();
        setShowDetailModal(false);
      }
    } catch (error) {
      showMessage('error', 'Failed to update');
    }
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditForm({
      booking_date: selectedAppointment.booking_date,
      booking_time: selectedAppointment.booking_time,
      service_provider_id: selectedAppointment.service_provider_id,
      status: selectedAppointment.status,
      customer_details: selectedAppointment.customer_details || {},
      booking_notes: selectedAppointment.booking_notes || ''
    });
  };

  const formatStatus = (status) => {
    // Convert status like "scheduled" or "no_show" to "Scheduled" or "No Show"
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDate();
    const month = date.toLocaleDateString('en-US', { month: 'short' });
    
    // Add ordinal suffix (st, nd, rd, th)
    const getOrdinalSuffix = (day) => {
      if (day > 3 && day < 21) return 'th';
      switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
      }
    };
    
    return `${day}${getOrdinalSuffix(day)} ${month}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes}${ampm}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
          {/* Header - Compact Design like AI In Review */}
          <div className="bg-white border-b border-gray-200">
            <div className="p-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-3">
                    Manage Appointments
                    <span className="text-xs font-normal text-gray-500">
                      Total: {appointments.length}
                    </span>
                  </h2>
                </div>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  {/* Service Provider Filter */}
                  <select
                    value={filters.service_provider_id}
                    onChange={(e) => {
                      setFilters({ ...filters, service_provider_id: e.target.value });
                      setCurrentPage(1);
                    }}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors appearance-none pr-8"
                    style={{ 
                      backgroundColor: primaryColor,
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                  >
                    <option value="" style={{ backgroundColor: 'white', color: 'black' }}>All Providers</option>
                    {serviceProviders.map((provider) => (
                      <option key={provider.id} value={provider.id} style={{ backgroundColor: 'white', color: 'black' }}>
                        {provider.full_name}
                      </option>
                    ))}
                  </select>

                  {/* Date Filter */}
                  <select
                    value={filters.date_filter}
                    onChange={(e) => {
                      setFilters({ ...filters, date_filter: e.target.value });
                      setCurrentPage(1);
                    }}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors appearance-none pr-8"
                    style={{ 
                      backgroundColor: primaryColor,
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                  >
                    {dateFilterOptions.map((option) => (
                      <option key={option.value} value={option.value} style={{ backgroundColor: 'white', color: 'black' }}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  {/* Status Filter */}
                  <select
                    value={filters.status}
                    onChange={(e) => {
                      setFilters({ ...filters, status: e.target.value });
                      setCurrentPage(1);
                    }}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors appearance-none pr-8"
                    style={{ 
                      backgroundColor: primaryColor,
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value} style={{ backgroundColor: 'white', color: 'black' }}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  {/* Appointment Type Filter */}
                  <select
                    value={filters.appointment_type}
                    onChange={(e) => {
                      setFilters({ ...filters, appointment_type: e.target.value });
                      setCurrentPage(1);
                    }}
                    className="text-white text-sm px-3 py-1.5 rounded-md font-medium focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors appearance-none pr-8"
                    style={{ 
                      backgroundColor: primaryColor,
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23ffffff' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                  >
                    {appointmentTypeOptions.map((option) => (
                      <option key={option.value} value={option.value} style={{ backgroundColor: 'white', color: 'black' }}>
                        {option.label}
                      </option>
                    ))}
                  </select>

                  {/* Page Size Selector */}
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="text-gray-700 text-sm px-3 py-1.5 rounded-md font-medium border border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-1 transition-colors appearance-none pr-8 bg-white"
                    style={{ 
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%23374151' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E")`,
                      backgroundPosition: 'right 6px center',
                      backgroundRepeat: 'no-repeat',
                      backgroundSize: '14px'
                    }}
                  >
                    <option value={10}>10</option>
                    <option value={15}>15</option>
                    <option value={20}>20</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {message.text && (
          <div className={`mb-6 p-4 rounded-lg flex items-center gap-2 ${
            message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
          }`}>
            {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
            {message.text}
          </div>
        )}

        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--theme-primary)] mx-auto mb-4"></div>
              <p className="text-gray-600">Loading appointments...</p>
            </div>
          ) : appointments.length === 0 ? (
            <div className="text-center py-12">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-600">No appointments found</p>
              <p className="text-sm text-gray-500 mt-2">Try adjusting your filters</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Appt #
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Service Provider
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {appointments.map((appointment) => (
                      <tr key={appointment.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-[rgb(41,173,211)]">{appointment.appointment_number || 'N/A'}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatDate(appointment.booking_date)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{formatTime(appointment.booking_time)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {(() => {
                            const isBoardingAppt = appointment.appointment_type === 'boarding' || appointment.reservation_type === 'boarding';
                            const isTourAppt = appointment.appointment_type === 'tour' || appointment.reservation_type === 'tour';
                            
                            // For tour appointments, show service provider name
                            if (isTourAppt) {
                              return (
                                <div className="text-sm text-gray-900">
                                  {appointment.service_provider_name || '-'}
                                </div>
                              );
                            }
                            
                            // For boarding appointments, show space name if available, otherwise show '-'
                            if (isBoardingAppt) {
                              return (
                                <div className="text-sm text-gray-900">
                                  {appointment.assigned_space_name || '-'}
                                </div>
                              );
                            }
                            
                            // For regular appointments, show service provider
                            return (
                              <>
                                <div className="text-sm text-gray-900">
                                  {appointment.service_provider_name || '-'}
                                </div>
                                {appointment.assigned_space_name && (
                                  <div className="text-sm text-gray-900 mt-0.5">
                                    {appointment.assigned_space_name}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {(() => {
                              // Check if it's a boarding or tour appointment
                              const isBoardingAppointment = appointment.appointment_type === 'boarding' || 
                                                             appointment.reservation_type === 'boarding';
                              const isTourAppointment = appointment.appointment_type === 'tour' || 
                                                         appointment.reservation_type === 'tour';
                              
                              if ((isBoardingAppointment || isTourAppointment) && appointment.form_details) {
                                // Use helper function to extract customer name
                                return extractCustomerName(appointment);
                              } else if (appointment.customer_details) {
                                // For regular appointments: get from customer_details
                                const firstName = appointment.customer_details.first_name || 
                                                appointment.customer_details['first_name'] ||
                                                appointment.customer_details.firstName || '';
                                const lastName = appointment.customer_details.last_name || 
                                               appointment.customer_details['last_name'] ||
                                               appointment.customer_details.lastName || '';
                                const fullName = `${firstName} ${lastName}`.trim();
                                return fullName || appointment.customer_details.name || '-';
                              }
                              return '-';
                            })()}
                          </div>
                          {(() => {
                            // Check for pet name first, then phone
                            const isBoardingAppointment = appointment.appointment_type === 'boarding' || 
                                                           appointment.reservation_type === 'boarding';
                            
                            let petName = '';
                            let phone = '';
                            
                            if (isBoardingAppointment && appointment.form_details) {
                              // For boarding: check form_details
                              petName = appointment.form_details.pet_name || 
                                      appointment.form_details['Pet Name'] || '';
                              phone = appointment.form_details.phone || 
                                    appointment.form_details['Phone'] || '';
                            } else if (appointment.customer_details) {
                              // For regular appointments: check customer_details
                              petName = appointment.customer_details.pet_name || 
                                      appointment.customer_details['pet_name'] ||
                                      appointment.customer_details.petName || '';
                              phone = appointment.customer_details.phone || 
                                    appointment.customer_details['phone'] || '';
                            }
                            
                            const displayText = petName || phone;
                            
                            return displayText ? (
                              <div className="text-sm text-gray-500">{displayText}</div>
                            ) : null;
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <select
                            value={appointment.status}
                            onChange={(e) => handleStatusChange(appointment.id, e.target.value)}
                            className="text-sm text-gray-700 border-0 bg-transparent focus:ring-0 p-0 cursor-pointer hover:text-[var(--theme-primary)]"
                          >
                            {statusOptions.filter(opt => opt.value !== '').map((option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <div className="flex items-center gap-3">
                            <button
                              onClick={() => handleViewDetails(appointment)}
                              className="text-[var(--theme-primary)] hover:text-[#229acc] transition-colors flex items-center gap-1"
                              title="View Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => {
                                handleViewDetails(appointment);
                                setTimeout(() => setIsEditMode(true), 100);
                              }}
                              className="text-theme-blue hover:text-blue-700 transition-colors flex items-center gap-1"
                              title="Edit"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openDeleteModal(appointment)}
                              className="text-red-600 hover:text-red-700 transition-colors flex items-center gap-1"
                              title="Delete"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-center">
                  <div className="flex items-center gap-2">
                    {/* Previous Button */}
                    <button
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                      title="Previous"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </button>

                    {/* Page Number Buttons */}
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 5;
                      
                      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                      
                      // Adjust start if we're near the end
                      if (endPage - startPage < maxVisiblePages - 1) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                      }
                      
                      // First page
                      if (startPage > 1) {
                        pages.push(
                          <button
                            key={1}
                            onClick={() => setCurrentPage(1)}
                            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                          >
                            1
                          </button>
                        );
                        if (startPage > 2) {
                          pages.push(<span key="ellipsis1" className="px-2 text-gray-500">...</span>);
                        }
                      }
                      
                      // Middle pages
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => setCurrentPage(i)}
                            className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${
                              currentPage === i
                                ? 'border-theme-blue bg-blue-50 text-theme-blue font-medium'
                                : 'border-gray-300 hover:bg-gray-50'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }
                      
                      // Last page
                      if (endPage < totalPages) {
                        if (endPage < totalPages - 1) {
                          pages.push(<span key="ellipsis2" className="px-2 text-gray-500">...</span>);
                        }
                        pages.push(
                          <button
                            key={totalPages}
                            onClick={() => setCurrentPage(totalPages)}
                            className="px-3 py-1.5 text-sm rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                          >
                            {totalPages}
                          </button>
                        );
                      }
                      
                      return pages;
                    })()}

                    {/* Next Button */}
                    <button
                      onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="p-2 rounded-lg border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
                      title="Next"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {showDetailModal && selectedAppointment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-[90vh] flex flex-col">
            {/* Sticky Header */}
            <div className="px-6 py-3 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: primaryColor }}>
              <div>
                <h3 className="text-base font-semibold text-white">
                  {isEditMode ? 'Edit Appointment' : 'Appointment Details'}
                </h3>
                {selectedAppointment.appointment_number && (
                  <p className="text-xs text-white text-opacity-90 mt-0.5">
                    #{selectedAppointment.appointment_number}
                  </p>
                )}
              </div>
              <button
                onClick={() => {
                  setShowDetailModal(false);
                  setIsEditMode(false);
                }}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Scrollable Content */}
            <div className="p-6 space-y-6 overflow-y-auto flex-1 scrollbar-thin">
              {/* Show loading state while fetching data */}
              {!modalDataReady ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-theme-blue mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading appointment data...</p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Render Boarding Appointment Details if it's a boarding type */}
                  {(selectedAppointment.appointment_type === 'boarding' || selectedAppointment.reservation_type === 'boarding' || selectedAppointment.appointment_type === 'tour' || selectedAppointment.reservation_type === 'tour') ? (
                    <div className="space-y-6">
                  {/* Date and Time Display */}
                  {(() => {
                    const isTour = selectedAppointment.appointment_type === 'tour' || selectedAppointment.reservation_type === 'tour';
                    
                    if (isTour) {
                      // Tour appointments: Show only Tour Schedule Date & Time
                      return (
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
                          <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            Tour Schedule Date & Time
                          </h4>
                          {isEditMode ? (
                            <div className="space-y-2">
                              <input
                                type="date"
                                value={editForm.dropoff_date || ''}
                                onChange={(e) => setEditForm({...editForm, dropoff_date: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              />
                              <input
                                type="time"
                                value={editForm.dropoff_time || ''}
                                onChange={(e) => setEditForm({...editForm, dropoff_time: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              />
                            </div>
                          ) : (
                            <>
                              <div className="text-sm font-semibold text-gray-900">
                                {selectedAppointment.dropoff_date && new Date(selectedAppointment.dropoff_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                              </div>
                              {selectedAppointment.dropoff_time && (
                                <div className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                                  <Clock className="h-3.5 w-3.5" />
                                  {selectedAppointment.dropoff_time}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    }
                    
                    // Boarding appointments: Show Drop-off and Pick-up
                    return (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Drop-off */}
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
                          <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            Drop-off
                          </h4>
                          {isEditMode ? (
                            <div className="space-y-2">
                              <input
                                type="date"
                                value={editForm.dropoff_date || ''}
                                onChange={(e) => setEditForm({...editForm, dropoff_date: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              />
                              <input
                                type="time"
                                value={editForm.dropoff_time || ''}
                                onChange={(e) => setEditForm({...editForm, dropoff_time: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              />
                            </div>
                          ) : (
                            <>
                              <div className="text-sm font-semibold text-gray-900">
                                {selectedAppointment.dropoff_date && new Date(selectedAppointment.dropoff_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                              </div>
                              {selectedAppointment.dropoff_time && (
                                <div className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                                  <Clock className="h-3.5 w-3.5" />
                                  {selectedAppointment.dropoff_time}
                                </div>
                              )}
                            </>
                          )}
                        </div>

                        {/* Pick-up */}
                        <div className="p-4 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border-2 border-green-200">
                          <h4 className="text-xs font-semibold text-green-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                            <Calendar className="h-3.5 w-3.5" />
                            Pick-up
                          </h4>
                          {isEditMode ? (
                            <div className="space-y-2">
                              <input
                                type="date"
                                value={editForm.pickup_date || ''}
                                onChange={(e) => setEditForm({...editForm, pickup_date: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              />
                              <input
                                type="time"
                                value={editForm.pickup_time || ''}
                                onChange={(e) => setEditForm({...editForm, pickup_time: e.target.value})}
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                              />
                            </div>
                          ) : (
                            <>
                              <div className="text-sm font-semibold text-gray-900">
                                {selectedAppointment.pickup_date && new Date(selectedAppointment.pickup_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                              </div>
                              {selectedAppointment.pickup_time && (
                                <div className="text-sm text-gray-700 mt-1 flex items-center gap-1">
                                  <Clock className="h-3.5 w-3.5" />
                                  {selectedAppointment.pickup_time}
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Service Provider for Tour Appointments */}
                  {(() => {
                    const isTour = selectedAppointment.appointment_type === 'tour' || selectedAppointment.reservation_type === 'tour';
                    if (isTour) {
                      return (
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <h4 className="text-sm font-semibold text-gray-700 mb-2">Service Provider</h4>
                          <div className="text-sm text-gray-900">{selectedAppointment.service_provider_name || 'Manager'}</div>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Service Details - Hide for Tour Appointments */}
                  {(() => {
                    const isTour = selectedAppointment.appointment_type === 'tour' || selectedAppointment.reservation_type === 'tour';
                    if (isTour) return null;
                    
                    return (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Package className="h-4 w-4" />
                          Service
                        </h4>
                    {isEditMode ? (
                      <div className="space-y-2">
                        {availableServices.length === 0 && !editForm.selected_service ? (
                          <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded p-2">
                            ⚠️ No services configured. Please configure services in Manage Boarding Calendar settings.
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <select
                              value={editForm.selected_service_id || ''}
                              onChange={(e) => handleServiceChange(e.target.value)}
                              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg"
                            >
                              <option value="">Select Service</option>
                              {/* Always show currently selected service first */}
                              {editForm.selected_service && (
                                <option key={editForm.selected_service.id} value={editForm.selected_service.id}>
                                  {editForm.selected_service.service_name || editForm.selected_service.name} (Current)
                                </option>
                              )}
                              {/* Show other available services */}
                              {availableServices.length > 0 ? (
                                availableServices
                                  .filter(s => s.id !== editForm.selected_service?.id) // Don't duplicate current service
                                  .map(service => (
                                    <option key={service.id} value={service.id}>
                                      {service.service_name || service.name}
                                    </option>
                                  ))
                              ) : (
                                <option disabled>No other services found</option>
                              )}
                            </select>
                            
                            {/* Edit Options Button - Only show if service has options */}
                            {editForm.selected_service_id && (() => {
                              // Get the full service object from availableServices
                              const serviceFromList = availableServices.find(s => s.id === editForm.selected_service_id);
                              const currentService = editForm.selected_service;
                              
                              // Check if service has options
                              const hasOptions = (serviceFromList?.options && serviceFromList.options.length > 0) || 
                                                 (currentService?.options && currentService.options.length > 0);
                              
                              if (!hasOptions) return null;
                              
                              return (
                                <button
                                  onClick={() => {
                                    const fullService = serviceFromList || currentService;
                                    const currentOptions = editForm.selected_service.selectedOptions || {};
                                    setSelectedServiceOptions(currentOptions);
                                    setShowOptionsModal(true);
                                  }}
                                  className="px-3 py-2 text-xs font-medium text-white rounded-lg whitespace-nowrap hover:opacity-90 transition-opacity"
                                  style={{ backgroundColor: primaryColor }}
                                >
                                  Edit Options
                                </button>
                              );
                            })()}
                          </div>
                        )}
                        
                        {availableServices.length === 0 && editForm.selected_service && Object.keys(serviceCategoryMappings).length === 0 && (
                          <div className="text-xs bg-amber-50 border border-amber-300 text-amber-800 p-2 rounded mt-1">
                            <strong>⚠️ Configuration Required:</strong><br/>
                            No services linked to space categories. To enable service changes:<br/>
                            1. Go to <strong>Manage Boarding Calendar</strong><br/>
                            2. Click <strong>&ldquo;Configure Services&rdquo;</strong> tab<br/>
                            3. Check space category boxes next to each service<br/>
                            4. Click <strong>&ldquo;Save All Settings&rdquo;</strong>
                          </div>
                        )}
                        {editForm.selected_service && (
                          <div className="mt-2 bg-blue-50 rounded px-3 py-2 space-y-2">
                            {/* Stay Duration */}
                            {editForm.dropoff_date && editForm.dropoff_time && editForm.pickup_date && editForm.pickup_time && (
                              <div className="flex items-center justify-between text-xs text-gray-600 pb-2 border-b border-blue-200">
                                <span>Stay Duration:</span>
                                <span className="font-semibold">
                                  {(() => {
                                    const duration = calculateStayDuration(
                                      editForm.dropoff_date,
                                      editForm.dropoff_time,
                                      editForm.pickup_date,
                                      editForm.pickup_time
                                    );
                                    const days = Math.floor(duration / 24);
                                    const hours = Math.floor(duration % 24);
                                    if (days > 0) {
                                      return hours > 0 
                                        ? `${days} day${days > 1 ? 's' : ''} ${hours} hr${hours !== 1 ? 's' : ''}`
                                        : `${days} day${days > 1 ? 's' : ''}`;
                                    }
                                    return `${duration.toFixed(1)} hours`;
                                  })()}
                                </span>
                              </div>
                            )}
                            
                            {/* Service Price */}
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-700">
                                {editForm.selected_service.is_variable ? 'Service (Duration-based)' : 'Service Price'}
                              </span>
                              <span className="font-bold text-gray-900">
                                ${(() => {
                                  const duration = calculateStayDuration(
                                    editForm.dropoff_date,
                                    editForm.dropoff_time,
                                    editForm.pickup_date,
                                    editForm.pickup_time
                                  );
                                  return calculateVariantPrice(editForm.selected_service, duration).toFixed(2);
                                })()}
                              </span>
                            </div>
                            
                            {/* Variant Breakdown */}
                            {editForm.selected_service.is_variable && editForm.selected_service.variants && (
                              <div className="pt-2 border-t border-blue-200 space-y-1">
                                {(() => {
                                  const duration = calculateStayDuration(
                                    editForm.dropoff_date,
                                    editForm.dropoff_time,
                                    editForm.pickup_date,
                                    editForm.pickup_time
                                  );
                                  const breakdown = getVariantBreakdown(editForm.selected_service, duration);
                                  return breakdown.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between text-xs text-gray-600">
                                      <span className="ml-2">→ {item.count}× {item.name} @ ${item.price.toFixed(2)}</span>
                                      <span className="font-medium">${(item.count * item.price).toFixed(2)}</span>
                                    </div>
                                  ));
                                })()}
                              </div>
                            )}
                            
                            {/* Selected Options Display */}
                            {editForm.selected_service.selectedOptions && Object.keys(editForm.selected_service.selectedOptions).length > 0 && (
                              <div className="pt-2 border-t border-blue-200 space-y-1">
                                <div className="text-xs font-semibold text-gray-700 mb-1">Selected Options:</div>
                                {editForm.selected_service.options?.map((option) => {
                                  const selectedValue = editForm.selected_service.selectedOptions[option.option_name];
                                  if (!selectedValue) return null;
                                  
                                  // Handle checkbox/single type
                                  if (selectedValue === true) {
                                    const optionPrice = parseFloat(option.price) || 0;
                                    return (
                                      <div key={option.option_id} className="flex items-center justify-between text-xs text-gray-600">
                                        <span className="ml-2">→ {option.option_name}</span>
                                        <span className="font-medium">
                                          {optionPrice > 0 ? `+$${optionPrice.toFixed(2)}` : 'Included'}
                                        </span>
                                      </div>
                                    );
                                  }
                                  
                                  // Handle dropdown type
                                  if (selectedValue.choice_id) {
                                    const choice = option.choices?.find(c => c.choice_id === selectedValue.choice_id);
                                    if (choice) {
                                      const choicePrice = parseFloat(choice.price) || 0;
                                      return (
                                        <div key={option.option_id} className="flex items-center justify-between text-xs text-gray-600">
                                          <span className="ml-2">→ {option.option_name}: {choice.choice_name}</span>
                                          <span className="font-medium">
                                            {choicePrice > 0 ? `+$${choicePrice.toFixed(2)}` : 'Included'}
                                          </span>
                                        </div>
                                      );
                                    }
                                  }
                                  
                                  return null;
                                })}
                              </div>
                            )}
                            
                            {/* Total Price (Service + Options + Tax - Token) */}
                            {(() => {
                              const duration = calculateStayDuration(
                                editForm.dropoff_date,
                                editForm.dropoff_time,
                                editForm.pickup_date,
                                editForm.pickup_time
                              );
                              const servicePrice = calculateVariantPrice(editForm.selected_service, duration);
                              
                              // Calculate options total
                              let optionsTotal = 0;
                              if (editForm.selected_service.selectedOptions) {
                                editForm.selected_service.options?.forEach((option) => {
                                  const selectedValue = editForm.selected_service.selectedOptions[option.option_name];
                                  if (selectedValue === true) {
                                    optionsTotal += parseFloat(option.price) || 0;
                                  } else if (selectedValue?.choice_id) {
                                    const choice = option.choices?.find(c => c.choice_id === selectedValue.choice_id);
                                    if (choice) {
                                      optionsTotal += parseFloat(choice.price) || 0;
                                    }
                                  }
                                });
                              }
                              
                              const subtotal = servicePrice + optionsTotal;
                              const taxAmount = taxSettings.tax_enabled ? (subtotal * (taxSettings.tax_rate || 0)) / 100 : 0;
                              const grandTotal = subtotal + taxAmount;
                              const tokenPaid = parseFloat(selectedAppointment?.payment_token_amount) || 0;
                              const pendingAmount = grandTotal - tokenPaid;
                              
                              return (
                                <>
                                  {taxSettings.tax_enabled && taxSettings.tax_rate > 0 && (
                                    <div className="pt-2 mt-2 border-t border-blue-200 flex items-center justify-between text-xs text-gray-600">
                                      <span>Tax ({taxSettings.tax_rate}%):</span>
                                      <span className="font-medium">${taxAmount.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {tokenPaid > 0 && (
                                    <div className="pt-2 mt-2 border-t border-blue-200 flex items-center justify-between text-xs text-green-600">
                                      <span>Token Amount Paid:</span>
                                      <span className="font-medium">-${tokenPaid.toFixed(2)}</span>
                                    </div>
                                  )}
                                  <div className="pt-2 mt-2 border-t-2 border-gray-300 flex items-center justify-between">
                                    <span className="text-sm font-bold text-gray-900">
                                      {tokenPaid > 0 ? 'Total (Pending):' : 'Total:'}
                                    </span>
                                    <span className="text-sm font-bold text-gray-900">
                                      ${tokenPaid > 0 ? pendingAmount.toFixed(2) : grandTotal.toFixed(2)}
                                    </span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="text-sm text-gray-900 font-medium mb-2">
                          {selectedAppointment.selected_service?.service_name || selectedAppointment.selected_service?.name || 'No service selected'}
                        </div>
                        {/* Duration-based pricing */}
                        {selectedAppointment.selected_service && (
                          <div className="bg-blue-50 rounded px-3 py-2 space-y-2">
                            {/* Stay Duration */}
                            {selectedAppointment.dropoff_date && selectedAppointment.dropoff_time && selectedAppointment.pickup_date && selectedAppointment.pickup_time && (
                              <div className="flex items-center justify-between text-xs text-gray-600 pb-2 border-b border-blue-200">
                                <span>Stay Duration:</span>
                                <span className="font-semibold">
                                  {(() => {
                                    const duration = calculateStayDuration(
                                      selectedAppointment.dropoff_date,
                                      selectedAppointment.dropoff_time,
                                      selectedAppointment.pickup_date,
                                      selectedAppointment.pickup_time
                                    );
                                    const days = Math.floor(duration / 24);
                                    const hours = Math.floor(duration % 24);
                                    if (days > 0) {
                                      return hours > 0 
                                        ? `${days} day${days > 1 ? 's' : ''} ${hours} hr${hours !== 1 ? 's' : ''}`
                                        : `${days} day${days > 1 ? 's' : ''}`;
                                    }
                                    return `${duration.toFixed(1)} hours`;
                                  })()}
                                </span>
                              </div>
                            )}
                            
                            {/* Service Price */}
                            <div className="flex items-center justify-between text-sm">
                              <span className="text-gray-700">
                                {selectedAppointment.selected_service.is_variable ? 'Service (Duration-based)' : 'Service Price'}
                              </span>
                              <span className="font-bold text-gray-900">
                                ${(() => {
                                  const duration = calculateStayDuration(
                                    selectedAppointment.dropoff_date,
                                    selectedAppointment.dropoff_time,
                                    selectedAppointment.pickup_date,
                                    selectedAppointment.pickup_time
                                  );
                                  return calculateVariantPrice(selectedAppointment.selected_service, duration).toFixed(2);
                                })()}
                              </span>
                            </div>
                            
                            {/* Variant Breakdown */}
                            {selectedAppointment.selected_service.is_variable && selectedAppointment.selected_service.variants && (
                              <div className="pt-2 border-t border-blue-200 space-y-1">
                                {(() => {
                                  const duration = calculateStayDuration(
                                    selectedAppointment.dropoff_date,
                                    selectedAppointment.dropoff_time,
                                    selectedAppointment.pickup_date,
                                    selectedAppointment.pickup_time
                                  );
                                  
                                  // Debug logging
                                  console.log('ManageAppointments - Variant Debug:', {
                                    duration,
                                    service: selectedAppointment.selected_service,
                                    variants: selectedAppointment.selected_service.variants
                                  });
                                  
                                  const breakdown = getVariantBreakdown(selectedAppointment.selected_service, duration);
                                  console.log('Breakdown result:', breakdown);
                                  
                                  return breakdown.map((item, idx) => (
                                    <div key={idx} className="flex items-center justify-between text-xs text-gray-600">
                                      <span className="ml-2">→ {item.count}× {item.name} @ ${item.price.toFixed(2)}</span>
                                      <span className="font-medium">${(item.count * item.price).toFixed(2)}</span>
                                    </div>
                                  ));
                                })()}
                              </div>
                            )}
                            
                            {/* Selected Options Display */}
                            {selectedAppointment.selected_service.selectedOptions && Object.keys(selectedAppointment.selected_service.selectedOptions).length > 0 && (
                              <div className="pt-2 border-t border-blue-200 space-y-1">
                                <div className="text-xs font-semibold text-gray-700 mb-1">Selected Options:</div>
                                {selectedAppointment.selected_service.options?.map((option) => {
                                  const selectedValue = selectedAppointment.selected_service.selectedOptions[option.option_name];
                                  if (!selectedValue) return null;
                                  
                                  // Handle checkbox/single type
                                  if (selectedValue === true) {
                                    const optionPrice = parseFloat(option.price) || 0;
                                    return (
                                      <div key={option.option_id} className="flex items-center justify-between text-xs text-gray-600">
                                        <span className="ml-2">→ {option.option_name}</span>
                                        <span className="font-medium">
                                          {optionPrice > 0 ? `+$${optionPrice.toFixed(2)}` : 'Included'}
                                        </span>
                                      </div>
                                    );
                                  }
                                  
                                  // Handle dropdown type
                                  if (selectedValue.choice_id) {
                                    const choice = option.choices?.find(c => c.choice_id === selectedValue.choice_id);
                                    if (choice) {
                                      const choicePrice = parseFloat(choice.price) || 0;
                                      return (
                                        <div key={option.option_id} className="flex items-center justify-between text-xs text-gray-600">
                                          <span className="ml-2">→ {option.option_name}: {choice.choice_name}</span>
                                          <span className="font-medium">
                                            {choicePrice > 0 ? `+$${choicePrice.toFixed(2)}` : 'Included'}
                                          </span>
                                        </div>
                                      );
                                    }
                                  }
                                  
                                  return null;
                                })}
                              </div>
                            )}
                            
                            {/* Total Price (Service + Options + Tax - Token) */}
                            {(() => {
                              const duration = calculateStayDuration(
                                selectedAppointment.dropoff_date,
                                selectedAppointment.dropoff_time,
                                selectedAppointment.pickup_date,
                                selectedAppointment.pickup_time
                              );
                              const servicePrice = calculateVariantPrice(selectedAppointment.selected_service, duration);
                              
                              // Calculate options total
                              let optionsTotal = 0;
                              if (selectedAppointment.selected_service.selectedOptions) {
                                selectedAppointment.selected_service.options?.forEach((option) => {
                                  const selectedValue = selectedAppointment.selected_service.selectedOptions[option.option_name];
                                  if (selectedValue === true) {
                                    optionsTotal += parseFloat(option.price) || 0;
                                  } else if (selectedValue?.choice_id) {
                                    const choice = option.choices?.find(c => c.choice_id === selectedValue.choice_id);
                                    if (choice) {
                                      optionsTotal += parseFloat(choice.price) || 0;
                                    }
                                  }
                                });
                              }
                              
                              const subtotal = servicePrice + optionsTotal;
                              const taxAmount = taxSettings.tax_enabled ? (subtotal * (taxSettings.tax_rate || 0)) / 100 : 0;
                              const grandTotal = subtotal + taxAmount;
                              const tokenPaid = parseFloat(selectedAppointment?.payment_token_amount) || 0;
                              const pendingAmount = grandTotal - tokenPaid;
                              
                              return (
                                <>
                                  {taxSettings.tax_enabled && taxSettings.tax_rate > 0 && (
                                    <div className="pt-2 mt-2 border-t border-blue-200 flex items-center justify-between text-xs text-gray-600">
                                      <span>Tax ({taxSettings.tax_rate}%):</span>
                                      <span className="font-medium">${taxAmount.toFixed(2)}</span>
                                    </div>
                                  )}
                                  {tokenPaid > 0 && (
                                    <div className="pt-2 mt-2 border-t border-blue-200 flex items-center justify-between text-xs text-green-600">
                                      <span>Token Amount Paid:</span>
                                      <span className="font-medium">-${tokenPaid.toFixed(2)}</span>
                                    </div>
                                  )}
                                  <div className="pt-2 mt-2 border-t-2 border-gray-300 flex items-center justify-between">
                                    <span className="text-sm font-bold text-gray-900">
                                      {tokenPaid > 0 ? 'Total (Pending):' : 'Total:'}
                                    </span>
                                    <span className="text-sm font-bold text-gray-900">
                                      ${tokenPaid > 0 ? pendingAmount.toFixed(2) : grandTotal.toFixed(2)}
                                    </span>
                                  </div>
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </>
                    )}
                      </div>
                    );
                  })()}

                  {/* Assigned Space - Hide for Tour Appointments */}
                  {(() => {
                    const isTour = selectedAppointment.appointment_type === 'tour' || selectedAppointment.reservation_type === 'tour';
                    if (isTour) return null;
                    
                    return (
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Building className="h-4 w-4" />
                          Assigned Space
                        </h4>
                    {isEditMode ? (
                      <div className="space-y-2">
                        {!editForm.selected_service_id ? (
                          <div className="text-xs text-gray-500 italic">Select a service first to see available spaces</div>
                        ) : filteredSpaces.length === 0 ? (
                          <div className="text-xs text-red-500">No spaces available for this service</div>
                        ) : (
                          <select
                            value={editForm.assigned_space_id || ''}
                            onChange={(e) => setEditForm({...editForm, assigned_space_id: e.target.value})}
                            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                          >
                            <option value="">Select Space</option>
                            {filteredSpaces.map(space => (
                              <option key={space.id} value={space.id}>
                                {space.space_name} {space.category_name ? `(${space.category_name})` : ''}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    ) : (
                      <div className="text-sm text-gray-900 font-medium">
                        {selectedAppointment.assigned_space_name || 'Not assigned'}
                      </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Form Details */}
                  {selectedAppointment.form_details && Object.keys(selectedAppointment.form_details).length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Your Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {(() => {
                          // Determine if it's a tour appointment
                          const isTour = selectedAppointment.appointment_type === 'tour' || selectedAppointment.reservation_type === 'tour';
                          // Use appropriate fields array
                          const fieldsArray = isTour ? tourFields : formFields;
                          
                          return Object.entries(selectedAppointment.form_details).map(([key, value]) => {
                            if (!value) return null;
                            const field = fieldsArray.find(f => f.id === key);
                            return (
                              <div key={key}>
                                <div className="text-xs text-gray-500">{field?.field_name || key}</div>
                                <div className="text-sm text-gray-900">{value}</div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Room Details */}
                  {selectedAppointment.room_details && Object.keys(selectedAppointment.room_details).length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Room Details</h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {Object.entries(selectedAppointment.room_details).map(([key, value]) => {
                          if (!value) return null;
                          const field = roomFields.find(f => f.id === key);
                          return (
                            <div key={key}>
                              <div className="text-xs text-gray-500">{field?.field_name || key}</div>
                              <div className="text-sm text-gray-900">{value}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Special Instructions */}
                  {selectedAppointment.special_instructions && Object.keys(selectedAppointment.special_instructions).length > 0 && (
                    <div className="p-4 bg-gray-50 rounded-lg">
                      <h4 className="text-sm font-semibold text-gray-700 mb-3">Special Instructions</h4>
                      <div className="space-y-2">
                        {Object.entries(selectedAppointment.special_instructions).map(([key, value]) => {
                          if (!value) return null;
                          const field = specialInstructionFields.find(f => f.id === key);
                          return (
                            <div key={key}>
                              <div className="text-xs text-gray-500">{field?.field_name || key}</div>
                              <div className="text-sm text-gray-900">{value}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Status */}
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Status</h4>
                    {isEditMode ? (
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                      >
                        <option value="confirmed">Confirmed</option>
                        <option value="checked_in">Checked In</option>
                        <option value="checked_out">Checked Out</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    ) : (
                      <div className="text-sm text-gray-900 capitalize">
                        {selectedAppointment.status?.replace(/_/g, ' ')}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                /* Regular Appointment Layout */
                <>
              {/* Grid Layout for Main Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Date */}
                <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
                  <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5" />
                    Date
                  </h4>
                  {isEditMode ? (
                    <input
                      type="date"
                      value={editForm.booking_date}
                      onChange={(e) => setEditForm({...editForm, booking_date: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                    />
                  ) : (
                    <div className="text-sm font-semibold text-gray-900">
                      {selectedAppointment.booking_date && new Date(selectedAppointment.booking_date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
                    </div>
                  )}
                </div>

                {/* Time */}
                <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border-2 border-blue-200">
                  <h4 className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2 flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    Time
                  </h4>
                  {isEditMode ? (
                    <input
                      type="time"
                      value={editForm.booking_time}
                      onChange={(e) => setEditForm({...editForm, booking_time: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                    />
                  ) : (
                    <div className="text-sm text-gray-700 flex items-center gap-1">
                      {formatTime(selectedAppointment.booking_time)}
                    </div>
                  )}
                </div>

                {/* Service Provider */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-500 mb-2 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Service Provider
                  </h4>
                  {isEditMode ? (
                    <select
                      value={editForm.service_provider_id}
                      onChange={(e) => setEditForm({...editForm, service_provider_id: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                    >
                      {serviceProviders.map(provider => (
                        <option key={provider.id} value={provider.id}>
                          {provider.full_name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-sm text-gray-900">{selectedAppointment.service_provider_name}</div>
                  )}
                </div>

                {/* Status */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-500 mb-2">Status</h4>
                  {isEditMode ? (
                    <select
                      value={editForm.status}
                      onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                    >
                      {statusOptions.filter(opt => opt.value !== '').map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div className="text-sm text-gray-900">
                      {formatStatus(selectedAppointment.status)}
                    </div>
                  )}
                </div>

                {/* Booked On */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="text-sm font-semibold text-gray-500 mb-2">Booked On</h4>
                  <div className="text-sm text-gray-900">
                    {(() => {
                      const date = new Date(selectedAppointment.created_at);
                      const day = date.getDate();
                      const month = date.toLocaleDateString('en-US', { month: 'short' });
                      const year = date.getFullYear();
                      const hours = date.getHours();
                      const minutes = String(date.getMinutes()).padStart(2, '0');
                      const ampm = hours >= 12 ? 'PM' : 'AM';
                      const hour12 = hours % 12 || 12;
                      
                      const getOrdinalSuffix = (day) => {
                        if (day > 3 && day < 21) return 'th';
                        switch (day % 10) {
                          case 1: return 'st';
                          case 2: return 'nd';
                          case 3: return 'rd';
                          default: return 'th';
                        }
                      };
                      
                      return `${day}${getOrdinalSuffix(day)} ${month} ${year}, ${hour12}:${minutes} ${ampm}`;
                    })()}
                  </div>
                </div>
              </div>

              {/* Service Details */}
              {selectedAppointment.service_details && (
                <div className="border-t pt-6">
                  <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                    <Package className="h-4 w-4" />
                    Service Details
                  </h4>
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="mb-3">
                      <div className="text-sm font-bold text-gray-900 mb-1">
                        {selectedAppointment.service_details.service_name}
                      </div>
                      <div className="text-xs text-gray-600">
                        Duration: {selectedAppointment.service_details.duration_minutes} minutes
                      </div>
                      {selectedAppointment.service_details.description && (
                        <div className="text-xs text-gray-600 mt-1">
                          {selectedAppointment.service_details.description}
                        </div>
                      )}
                    </div>

                    {/* Pricing */}
                    {selectedAppointment.selected_service?.selectedVariant ? (
                      <div className="border-t border-blue-200 pt-3 mt-3">
                        <div className="text-xs font-medium text-gray-600 mb-2">Selected Option:</div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-900">
                            {selectedAppointment.selected_service.selectedVariant.variant_type}: {' '}
                            <span className="font-medium">{selectedAppointment.selected_service.selectedVariant.variant_name}</span>
                          </span>
                          <span className="text-sm font-bold text-green-700">
                            ${selectedAppointment.selected_service.selectedVariant.price.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ) : selectedAppointment.service_details.price && !selectedAppointment.service_details.is_variable ? (
                      <div className="border-t border-blue-200 pt-3 mt-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Service Price:</span>
                          <span className="text-sm font-bold text-green-700">
                            ${selectedAppointment.service_details.price.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    ) : null}

                    {/* Additional Options */}
                    {selectedAppointment.selected_service?.selectedOptions && 
                     Object.keys(selectedAppointment.selected_service.selectedOptions).length > 0 && (
                      <div className="border-t border-blue-200 pt-3 mt-3">
                        <div className="text-xs font-medium text-gray-600 mb-2">Additional Options:</div>
                        <div className="space-y-2">
                          {Object.entries(selectedAppointment.selected_service.selectedOptions).map(([key, value]) => {
                            if (!value) return null;
                            
                            // Find option details from service
                            const option = selectedAppointment.service_details.options?.find(opt => opt.option_name === key);
                            if (!option) return null;
                            
                            // For checkbox options
                            if (option.option_type === 'single' && value === true) {
                              return (
                                <div key={key} className="flex items-center justify-between text-sm">
                                  <span className="text-gray-700">• {key}</span>
                                  {option.price && (
                                    <span className="text-green-700 font-medium">+${option.price.toFixed(2)}</span>
                                  )}
                                </div>
                              );
                            }
                            
                            // For dropdown options
                            if (option.option_type === 'dropdown' && value) {
                              const choice = option.choices?.find(c => c.choice_name === value);
                              return (
                                <div key={key} className="flex items-center justify-between text-sm">
                                  <span className="text-gray-700">• {key}: {value}</span>
                                  {choice?.price && (
                                    <span className="text-green-700 font-medium">+${choice.price.toFixed(2)}</span>
                                  )}
                                </div>
                              );
                            }
                            
                            return null;
                          })}
                        </div>
                      </div>
                    )}

                    {/* Total Service Cost with Duration-based Pricing */}
                    {selectedAppointment.selected_service && (
                      <div className="border-t border-blue-200 pt-3 mt-3">
                        <div className="flex items-center justify-between bg-gradient-to-r from-green-50 to-blue-50 p-3 rounded border-2 border-green-200">
                          <span className="text-sm font-bold text-gray-900">Total Service Cost</span>
                          <div className="text-lg font-bold text-green-700">
                            ${(() => {
                              // Calculate duration-based price
                              const duration = calculateStayDuration(
                                selectedAppointment.dropoff_date,
                                selectedAppointment.dropoff_time,
                                selectedAppointment.pickup_date,
                                selectedAppointment.pickup_time
                              );
                              let total = calculateVariantPrice(selectedAppointment.selected_service, duration);
                              
                              // Add options
                              if (selectedAppointment.selected_service?.selectedOptions) {
                                Object.entries(selectedAppointment.selected_service.selectedOptions).forEach(([key, value]) => {
                                  const option = selectedAppointment.service_details?.options?.find(opt => opt.option_name === key);
                                  if (option) {
                                    if (option.option_type === 'single' && value === true && option.price) {
                                      total += option.price;
                                    } else if (option.option_type === 'dropdown' && value) {
                                      const choice = option.choices?.find(c => c.choice_name === value);
                                      if (choice?.price) {
                                        total += choice.price;
                                      }
                                    }
                                  }
                                });
                              }
                              return total.toFixed(2);
                            })()}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Customer Details */}
              <div className="border-t pt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Customer Details
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {customFields
                    .filter(field => {
                      // Filter out fields with underscores or special naming patterns
                      if (!selectedAppointment.customer_details || !selectedAppointment.customer_details[field.field_key]) {
                        return false;
                      }
                      // Exclude fields with excessive underscores or special characters
                      const hasExcessiveUnderscores = (field.field_key.match(/_/g) || []).length > 2;
                      const hasSpecialPattern = field.field_key.includes('_for_') || field.field_key.includes('_-_');
                      return !hasExcessiveUnderscores && !hasSpecialPattern;
                    })
                    .map((field) => (
                      <div key={field.id} className="p-3 bg-gray-50 rounded-lg">
                        <div className="text-sm font-medium text-gray-500 mb-1">
                          {field.field_name}
                        </div>
                        <div className="text-sm text-gray-900">
                          {selectedAppointment.customer_details[field.field_key] || 'N/A'}
                        </div>
                      </div>
                    ))}
                </div>
              </div>

              {/* Notes */}
              <div className="border-t pt-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Notes</h4>
                {isEditMode ? (
                  <textarea
                    value={editForm.booking_notes}
                    onChange={(e) => setEditForm({...editForm, booking_notes: e.target.value})}
                    rows={3}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--theme-primary)] focus:border-transparent"
                    placeholder="Add notes..."
                  />
                ) : (
                  <div className="text-gray-700 text-sm bg-gray-50 p-3 rounded-lg">
                    {selectedAppointment.booking_notes || 'No notes'}
                  </div>
                )}
              </div>

              {/* Payment Info */}
                </>
              )}
                </>
              )}
            </div>

            {/* Sticky Footer - Compact */}
            <div className="px-4 py-2 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-white">
              {isEditMode ? (
                <>
                  <button
                    onClick={() => openDeleteModal(selectedAppointment)}
                    className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-1.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                  <button
                    onClick={handleSaveEdit}
                    className="px-4 py-1.5 text-sm text-white rounded-lg font-medium transition-colors flex items-center gap-1.5"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Save className="h-3.5 w-3.5" />
                    Save Changes
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={() => openDeleteModal(selectedAppointment)}
                    className="px-3 py-1.5 text-sm text-white bg-red-600 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-1.5"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Delete
                  </button>
                  <button
                    onClick={handleEditAppointment}
                    className="px-4 py-1.5 text-sm text-white rounded-lg font-medium transition-colors flex items-center gap-1.5"
                    style={{ backgroundColor: primaryColor }}
                  >
                    <Edit className="h-3.5 w-3.5" />
                    Edit
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && appointmentToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Confirm Delete</h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete this appointment? This action cannot be undone.
              </p>
              <div className="bg-gray-50 rounded-lg p-3 mb-4">
                <div className="text-sm">
                  <div className="font-medium text-gray-900">
                    {formatDate(appointmentToDelete.booking_date)} at {formatTime(appointmentToDelete.booking_time)}
                  </div>
                  <div className="text-gray-600">
                    {appointmentToDelete.service_provider_name}
                  </div>
                  <div className="text-gray-600">
                    {(() => {
                      // Check if it's a boarding appointment
                      const isBoardingAppointment = appointmentToDelete.appointment_type === 'boarding' || 
                                                     appointmentToDelete.reservation_type === 'boarding';
                      
                      if (isBoardingAppointment && appointmentToDelete.form_details) {
                        // Use helper function to extract customer name
                        const customerName = extractCustomerName(appointmentToDelete);
                        return customerName !== '-' ? customerName : 'Unknown Customer';
                      } else if (appointmentToDelete.customer_details) {
                        // For regular appointments: get from customer_details
                        const firstName = appointmentToDelete.customer_details.first_name || 
                                        appointmentToDelete.customer_details['first_name'] ||
                                        appointmentToDelete.customer_details.firstName || '';
                        const lastName = appointmentToDelete.customer_details.last_name || 
                                       appointmentToDelete.customer_details['last_name'] ||
                                       appointmentToDelete.customer_details.lastName || '';
                        const fullName = `${firstName} ${lastName}`.trim();
                        return fullName || appointmentToDelete.customer_details.name || 'Unknown Customer';
                      }
                      return 'Unknown Customer';
                    })()}
                  </div>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={closeDeleteModal}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAppointment}
                className="px-4 py-2 text-white bg-red-600 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete All Appointments Confirmation Modal */}
      {showDeleteAllModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <AlertCircle className="h-5 w-5 text-red-600" />
                Delete All Appointments
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-600 mb-4">
                Are you sure you want to delete <strong>ALL {appointments.length} appointments</strong>? This action cannot be undone.
              </p>
              <div className="bg-red-50 rounded-lg p-4 mb-4 border-2 border-red-200">
                <div className="flex items-start gap-3">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="text-sm">
                    <div className="font-semibold text-red-900 mb-1">
                      Warning: Permanent Data Loss
                    </div>
                    <ul className="text-red-700 space-y-1 text-xs">
                      <li>• All appointment records will be permanently deleted</li>
                      <li>• Customer details and booking history will be lost</li>
                      <li>• This action cannot be reversed</li>
                    </ul>
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-600">
                Note: The appointment counter will not be affected. To reset the counter, go to Manage Appointment Calendar → Settings tab.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowDeleteAllModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAllAppointments}
                className="px-4 py-2 text-white bg-red-600 rounded-lg font-medium hover:bg-red-700 transition-colors flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete All {appointments.length} Appointments
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Service Options Modal */}
      {showOptionsModal && editForm.selected_service && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0" style={{ backgroundColor: `${primaryColor}10` }}>
              <h3 className="text-lg font-bold text-gray-900">
                Service Options: {editForm.selected_service.service_name}
              </h3>
              <button
                onClick={() => setShowOptionsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Content - Scrollable */}
            <div className="px-6 py-4 overflow-y-auto flex-1 scrollbar-thin">
              <p className="text-sm text-gray-600 mb-4">
                Review and select options for your service. Previously selected options have been carried over where available.
              </p>
              
              <div className="space-y-4">
                {editForm.selected_service.options?.map((option) => (
                  <div key={option.option_id} className="border border-gray-200 rounded-lg p-4">
                    <label className="text-sm font-semibold text-gray-900 mb-2 block">
                      {option.option_name}
                      {option.is_required && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    
                    {option.option_type === 'single' || option.option_type === 'checkbox' ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={selectedServiceOptions[option.option_name] === true}
                          onChange={(e) => handleOptionChange(option.option_name, e.target.checked)}
                          className="h-4 w-4 rounded border-gray-300"
                          style={{ accentColor: primaryColor }}
                        />
                        <span className="text-sm text-gray-700">
                          {option.price > 0 && `+$${option.price}`}
                        </span>
                      </div>
                    ) : option.option_type === 'dropdown' ? (
                      <select
                        value={selectedServiceOptions[option.option_name]?.choice_id || ''}
                        onChange={(e) => {
                          const choice = option.choices.find(c => c.choice_id === e.target.value);
                          handleOptionChange(option.option_name, choice ? { choice_id: choice.choice_id, choice_name: choice.choice_name } : null);
                        }}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                      >
                        <option value="">Select...</option>
                        {option.choices?.map((choice) => (
                          <option key={choice.choice_id} value={choice.choice_id}>
                            {choice.choice_name} {choice.price > 0 && `(+$${choice.price})`}
                          </option>
                        ))}
                      </select>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
            
            {/* Footer */}
            <div className="px-6 py-4 bg-gray-50 flex justify-end gap-2 border-t border-gray-200 flex-shrink-0">
              <button
                onClick={() => setShowOptionsModal(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Skip Options
              </button>
              <button
                onClick={handleOptionsConfirm}
                className="px-4 py-2 text-sm font-semibold text-white rounded-lg"
                style={{ backgroundColor: primaryColor }}
              >
                Confirm Options
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAppointments;
